<script>
    import SidebarTab from "../Sidebar/SidebarTab.svelte";
    import { user } from "../../../stores";
    import Hamburger from "./Hamburger.svelte";
    import { fly } from "svelte/transition";
    import { goToURL } from "../../../Utils/utilFunctions.svelte";
    import ExpandableTab from "../Sidebar/ExpandableTab.svelte";
    import { onMount } from "svelte";
    import { CheckFollowUpItemInterval, exportIcon } from "../../../Utils/utilConstants.svelte";
    import { apiMethods } from "../../../Services/index.svelte";
    import { DirectUserRoles } from "../../../Utils/utilEnums.svelte";

    export let sidebarExpanded;


    let followUpNumbers = {
        consentDue: 0,
        patientPaymentsDue: 0,
        moreThan30Days: 0,
        claimError: 0,
        underPaid: 0,
        overPaid: 0,
    }

    onMount(()=> {
        checkFollowUpItems();
        setInterval(() => {
            checkFollowUpItems();
        }, CheckFollowUpItemInterval);
    })

    const checkFollowUpItems = () => {
        apiMethods.getMethod.getNumberOfFollowUpItems()
            .then((res) => handleFollowUpItemCounts(res))
            .catch((err) => console.log(err))
    }

    const handleFollowUpItemCounts = (res) => {
        followUpNumbers = res;
    }

    $: totalFollowUp = countTotal(followUpNumbers);

    const countTotal = (obj) => {
        if (obj && typeof(obj) === "object") return Object.values(obj).reduce((sum, val) => sum + val, 0);
        else return 0;
    }

    // HELPER FUNCTIONS
    const _checkIfShowCollapseTab = (pathArr = []) => {
        if (!pathArr.length) return false;

        return !pathArr.every((path) => !_checkIfShowTab($user.role, path));
    };

    const _checkIfShowTab = (role, path) => {
        // role 100 - Admin
        // role 90 - Developer
        // role 80 - Manager
        // role 20 - Support
        // role 2 - Doctor
        // role 1 - Patient

        switch (path) {
            case "Admin":
            case "Reconciliations":
            case "Announcements":
            case "Groups":
            case "ConsentDue":
            case "PatientPaymentDue":
            case "FollowUp":
            case "ClaimError":
            case "Underpaid":
            case "Overpaid":
            case "SupportStaff":
                return role === 100;

            case "ExceptionLogs":
                return role >= 90;

            case "Calendar":
            case "CalendarCategories":
            case "GlobalEvents":
                return role >= 80;

            case "Account":
            case "Patient":
            case "AllProviders":
            case "InsuranceFunds":
            case "WorkCoverAuthorities":
            case "TrafficAccidentAuthorities":
            case "Hospitals":
            case "MBSItems":
            case "ASAItems":
            case "PublicHolidays":
                return role >= 20;

            case "AccountUsage":
            case "AccountInvoice":
            case "EmailTemplates":
            case "SmsTemplates":
            case "PdfTemplates":
                return role >= 20 && role != 80;

            case "ActivityLogs":
            case "EclipseLogs":
                return role >= 2 && role != 80;

            case "AccountSchedules":
                return role == 100 || role == 20 || role == 2;

            case "Reports":
                return (
                    (
                        window.location.hostname.split(".")[0] === "app" ||
                        window.location.hostname.split(".")[0] === "new"
                    ) &&
                    role === DirectUserRoles.Admin
                );

            // Everyone
            case "Estimates":
            case "Invoices":
            case "PatientEncounter":
            case "PatientQuote":
            case "Receipts":
            default:
                return true;
        }
    };

    const RedirectToDashboard = () => {
        sidebarExpanded = false;
        goToURL("/");
    };
</script>

<div
    class="absolute top-0 left-0 flex flex-col overflow-y-auto overflow-x-hidden z-[100] {sidebarExpanded
        ? 'w-full h-full bg-white'
        : 'h-16 w-14'}"
>
    <div class="ml-3 mt-5 flex flex-row mb-2">
        <Hamburger bind:open={sidebarExpanded} />

        {#if sidebarExpanded}
            <div
                class="flex items-center cursor-pointer gap-x-2"
                on:click|preventDefault={RedirectToDashboard}
            >
                <img
                    src="/imgs/Logo.png"
                    alt=""
                    class="bg-gray-200 w-10 h-10 rounded-full border-2"
                />
                <p class="w-auto wrap text-black">
                    Anaemate Management Dashboard
                </p>
            </div>
        {/if}
    </div>

    {#if sidebarExpanded}
        <div
            class="sidebar pl-3 relative flex flex-1 flex-col items-left justify-between"
            in:fly={{ duration: 300, x: -200 }}
            out:fly={{ duration: 300, x: 200 }}
        >
            <div
                class="flex flex-col absolute overflow-y-auto overflow-x-hidden inset-0"
            >
                <ul
                    class={sidebarExpanded
                        ? "menu w-54 pr-2 py-2 pt-0"
                        : "menu pr-2 py-2 pt-0"}
                >
                    <ExpandableTab
                        title="Doctors"
                        iFont="fa fa-user-doctor"
                        {sidebarExpanded}
                        isShow={_checkIfShowCollapseTab([
                            "Account",
                            "Groups",
                            "AccountSchedules",
                            "AccountUsage",
                            "AccountInvoice",
                        ])}
                        routesArr={[
                            "Account",
                            "Groups",
                            "AccountSchedules",
                            "AccountUsage",
                            "AccountInvoice",
                        ]}
                    >
                        <SidebarTab
                            route="Account"
                            title="Doctors"
                            iFont="fa fa-user-doctor"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Account")}
                        />

                        <SidebarTab
                            route="Groups"
                            title="Groups"
                            iFont="fa-solid fa-users-viewfinder fa-sm"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Groups")}
                        />

                        <SidebarTab
                            route="AccountSchedules"
                            title="Doctor Schedules"
                            iFont="fa-solid fa-calendar-day"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "AccountSchedules",
                            )}
                        />

                        <SidebarTab
                            route="AccountUsage"
                            title="Doctors Usage"
                            iFont="fa fa-notes-medical"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "AccountUsage")}
                        />

                        <SidebarTab
                            route="AccountInvoice"
                            title="Doctors Invoices"
                            iFont="fa fa-file-invoice-dollar"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "AccountInvoice",
                            )}
                        />
                    </ExpandableTab>

                    <SidebarTab
                        route="Patient"
                        title="Patients"
                        iFont="fa fa-user-injured"
                        {sidebarExpanded}
                        isShow={_checkIfShowTab($user.role, "Patient")}
                    />

                    <SidebarTab
                        route="PatientEncounter"
                        title="Patient Encounters"
                        iFont="fa fa-hand-holding-medical"
                        {sidebarExpanded}
                        isShow={_checkIfShowTab($user.role, "PatientEncounter")}
                    />

                    <SidebarTab
                        route="PatientQuote"
                        title="Patient Quoting"
                        iFont="fa-solid fa-person-circle-question"
                        {sidebarExpanded}
                        isShow={_checkIfShowTab($user.role, "PatientQuote")}
                    />

                    <SidebarTab
                        route="Calendar"
                        title="Group Calendar"
                        iFont="fa-solid fa-calendar-days"
                        {sidebarExpanded}
                        isShow={_checkIfShowTab($user.role, "Calendar")}
                    />

                    <ExpandableTab
                        title={"Follow Up (" + totalFollowUp + ")" }
                        iFont="fa-solid fa-clock"
                        {sidebarExpanded}
                        isShow={_checkIfShowCollapseTab([
                            "ConsentDue",
                            "PatientPaymentDue",
                            "FollowUp",
                            "ClaimError",
                            "Underpaid",
                            "Overpaid",
                        ])}
                        routesArr={["ConsentDue", "PatientPaymentDue", "FollowUp", "ClaimError", "Underpaid", "Overpaid"]}
                    >
                        <SidebarTab
                            route="ConsentDue"
                            title={"Consent Due (" + (followUpNumbers.consentDue ?? 0) + ")" }
                            iFont="fa-solid fa-check-double"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "ConsentDue")}
                        />
                        <SidebarTab
                            route="PatientPaymentDue"
                            title={"Patient Payment Due (" + (followUpNumbers.patientPaymentsDue ?? 0) + ")" }
                            iFont="fa-solid fa-hourglass-end"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "PatientPaymentDue")}
                        />
                        <SidebarTab
                            route="FollowUp"
                            title={"> 30 Days (" + (followUpNumbers.moreThan30Days ?? 0) + ")" }
                            iFont="fa-solid fa-clock-rotate-left"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "FollowUp")}
                        />
                        <SidebarTab
                            route="ClaimError"
                            title={"Claim Errors (" + (followUpNumbers.claimError ?? 0) + ")" }
                            iFont="fa-solid fa-file-circle-xmark"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "ClaimError")}
                        />
                        <SidebarTab
                            route="Underpaid"
                            title={"Underpaid (" + (followUpNumbers.underPaid ?? 0) + ")" }
                            iFont="fa-solid fa-scale-unbalanced"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Underpaid")}
                        />
                        <SidebarTab
                            route="Overpaid"
                            title={"OverPaid (" + (followUpNumbers.overPaid ?? 0) + ")" }
                            iFont="fa-solid fa-scale-unbalanced-flip"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Overpaid")}
                        />
                    </ExpandableTab>

                    <ExpandableTab
                        title="Billing"
                        iFont="fa fa-receipt"
                        {sidebarExpanded}
                        isShow={_checkIfShowCollapseTab([
                            "Estimates",
                            "Invoices",
                            "Receipts",
                            "Reconciliations",
                        ])}
                        routesArr={[
                            "Estimates",
                            "Invoices",
                            "Receipts",
                            "Reconciliations",
                        ]}
                    >
                        <SidebarTab
                            route="Estimates"
                            title="Estimates"
                            iFont="fa fa-file-invoice"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Estimates")}
                        />

                        <SidebarTab
                            route="Invoices"
                            title="Invoices"
                            iFont="fa fa-file-invoice-dollar"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Invoices")}
                        />

                        <SidebarTab
                            route="Receipts"
                            title="Payments"
                            iFont="fa fa-receipt"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Receipts")}
                        />

                        <SidebarTab
                            route="Reconciliations"
                            title="Reconciliation"
                            iFont="fa-solid fa-list-check"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "Reconciliations",
                            )}
                        />
                    </ExpandableTab>

                    <SidebarTab
                        route="Reports"
                        title="Reports"
                        iFont={exportIcon}
                        {sidebarExpanded}
                        isShow={_checkIfShowTab($user.role, "Reports")}
                    />

                    <ExpandableTab
                        title="Medical Data"
                        iFont="fa fa-house-medical"
                        {sidebarExpanded}
                        isShow={_checkIfShowCollapseTab([
                            "AllProviders",
                            "Hospitals",
                            "InsuranceFunds",
                            "WorkCoverAuthorities",
                            "TrafficAccidentAuthorities",
                            "MBSItems",
                            "ASAItems",
                        ])}
                        routesArr={[
                            "AllProviders",
                            "Hospitals",
                            "InsuranceFunds",
                            "WorkCoverAuthorities",
                            "TrafficAccidentAuthorities",
                            "MBSItems",
                            "ASAItems",
                        ]}
                    >
                        <SidebarTab
                            route="AllProviders"
                            title="All Providers"
                            iFont="fa fa-user-nurse"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "AllProviders")}
                        />

                        <SidebarTab
                            route="Hospitals"
                            title="Hospitals"
                            iFont="fa fa-house-medical"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Hospitals")}
                        />

                        <SidebarTab
                            route="InsuranceFunds"
                            title="Insurance Funds"
                            iFont="fa fa-hospital"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "InsuranceFunds",
                            )}
                        />

                        <SidebarTab
                            route="WorkCoverAuthorities"
                            title="Work Cover Authorities"
                            iFont="fa-solid fa-helmet-safety"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "WorkCoverAuthorities",
                            )}
                        />

                        <SidebarTab
                            route="TrafficAccidentAuthorities"
                            title="Traffic Accident Authorities"
                            iFont="fas fa-car-crash"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "TrafficAccidentAuthorities",
                            )}
                        />

                        <SidebarTab
                            route="MBSItems"
                            title="MBS Items"
                            iFont="fa fa-list"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "MBSItems")}
                        />

                        <SidebarTab
                            route="ASAItems"
                            title="ASA Items"
                            iFont="fa fa-list"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "ASAItems")}
                        />
                    </ExpandableTab>

                    <ExpandableTab
                        title="System"
                        iFont="fa fa-toolbox"
                        {sidebarExpanded}
                        isShow={_checkIfShowCollapseTab([
                            "Admin",
                            "PublicHolidays",
                            "GlobalEvents",
                            "Announcements",
                            "CalendarCategories",
                        ])}
                        routesArr={[
                            "Admin",
                            "PublicHolidays",
                            "GlobalEvents",
                            "Announcements",
                            "CalendarCategories",
                        ]}
                    >
                        <SidebarTab
                            route="Admin"
                            title="Admins"
                            iFont="far fa-user-circle"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "Admin")}
                        />
                        
                        <SidebarTab
                            route="CalendarCategories"
                            title="Calendar Categories"
                            iFont="fa fa-list-alt"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "CalendarCategories",
                            )}
                        />

                        <SidebarTab
                            route="GlobalEvents"
                            title="Global Events"
                            iFont="fa-solid fa-globe"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "GlobalEvents",
                            )}
                        />

                        <SidebarTab
                            route="PublicHolidays"
                            title="Public Holidays"
                            iFont="fa fa-menorah"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "PublicHolidays",
                            )}
                        />

                        <SidebarTab
                            route="Announcements"
                            title="Announcements"
                            iFont="fa-solid fa-bullhorn"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "Announcements",
                            )}
                        />
                    </ExpandableTab>

                    <ExpandableTab
                        title="Templates"
                        iFont="fa fa-file-pen"
                        {sidebarExpanded}
                        isShow={_checkIfShowCollapseTab([
                            "EmailTemplates",
                            "SmsTemplates",
                            "PdfTemplates",
                        ])}
                        routesArr={[
                            "EmailTemplates",
                            "SmsTemplates",
                            "PdfTemplates",
                        ]}
                    >
                        <SidebarTab
                            route="EmailTemplates"
                            title="Email Templates"
                            iFont="fa fa-envelope"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "EmailTemplates",
                            )}
                        />

                        <SidebarTab
                            route="SmsTemplates"
                            title="Sms Templates"
                            iFont="fa fa-comment"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "SmsTemplates")}
                        />

                        <SidebarTab
                            route="PdfTemplates"
                            title="PDF Templates"
                            iFont="fa fa-file-pdf"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "PdfTemplates")}
                        />
                    </ExpandableTab>

                    <ExpandableTab
                        title="Logs"
                        iFont="fa fa-folder-closed"
                        {sidebarExpanded}
                        isShow={_checkIfShowCollapseTab([
                            "ActivityLogs",
                            "EclipseLogs",
                            "ExceptionLogs",
                        ])}
                        routesArr={[
                            "ActivityLogs",
                            "EclipseLogs",
                            "ExceptionLogs",
                        ]}
                    >
                        <SidebarTab
                            route="ActivityLogs"
                            title="Activity Logs"
                            iFont="fa fa-folder-closed"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "ActivityLogs")}
                        />

                        <SidebarTab
                            route="EclipseLogs"
                            title="Eclipse Logs"
                            iFont="fa fa-folder-closed"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab($user.role, "EclipseLogs")}
                        />

                        <SidebarTab
                            route="ExceptionLogs"
                            title="Exception Logs"
                            iFont="fa fa-folder-closed"
                            {sidebarExpanded}
                            isShow={_checkIfShowTab(
                                $user.role,
                                "ExceptionLogs",
                            )}
                        />
                    </ExpandableTab>
                </ul>
            </div>
        </div>
    {/if}
</div>

<style>
    .sidebar {
        transition: 0.3s linear;
    }
</style>
